import { HttpClient } from "@angular/common/http";
import { Inject, Injectable, InjectionToken, Optional } from "@angular/core";
import { SwPush } from "@angular/service-worker";
import { Actions, ofActionSuccessful, Store } from "@ngxs/store";
import { BehaviorSubject, EMPTY, firstValueFrom, Observable } from "rxjs";
import { catchError, distinctUntilChanged, map, switchMap, take } from "rxjs/operators";

import { PushNotificationPayload } from "../models/push-notification.model";
import { PushNotificationActions } from "../states/push-notification.actions";

export interface PushNotificationConfig {
    vapidPublicKey: string;
    apiBaseUrl: string;
    enabled?: boolean;
}

export const PUSH_NOTIFICATION_CONFIG = new InjectionToken<PushNotificationConfig>("PushNotificationConfig");

const SUBSCRIPTION_ID_STORAGE_KEY = "ha_push_subscription_id";

@Injectable({ providedIn: "root" })
export class PushNotificationsService {
    private _isSubscribedSubject = new BehaviorSubject<boolean>(false);
    private _isEnablingInProgress = false;

    constructor(
        private swPush: SwPush,
        private http: HttpClient,
        private store: Store,
        private actions$: Actions,
        @Optional() @Inject(PUSH_NOTIFICATION_CONFIG) private config?: PushNotificationConfig,
    ) {
        if (this.isSupported) {
            this.swPush.subscription.pipe(map((sub) => !!sub), distinctUntilChanged()).subscribe((isSubscribed) => {
                this._isSubscribedSubject.next(isSubscribed);
            });

            // Listen for clear actions to clean up IndexedDB
            this.actions$.pipe(ofActionSuccessful(PushNotificationActions.ClearAllNotifications)).subscribe(() => {
                this.clearAllNotificationsFromIndexedDB().catch((error) =>
                    console.error("Failed to clear notifications from IndexedDB", error)
                );
            });

            this.actions$.pipe(ofActionSuccessful(PushNotificationActions.MarkAllAsRead)).subscribe(() => {
                this.clearAllNotificationsFromIndexedDB().catch((error) =>
                    console.error("Failed to clear notifications from IndexedDB after marking all as read", error)
                );
            });

            // Listen for status changes to update IndexedDB
            this.actions$.pipe(ofActionSuccessful(PushNotificationActions.SetNotificationStatus)).subscribe(
                (action: PushNotificationActions.SetNotificationStatus) => {
                    this.updateNotificationStatusInIndexedDB(action.notificationId, action.status).catch((error) =>
                        console.error("Failed to update notification status in IndexedDB", error)
                    );
                },
            );
        }
    }

    get isSupported(): boolean {
        return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    }

    get isEnabled(): boolean {
        return this.config?.enabled !== false && this.isSupported;
    }

    get isSubscribed(): boolean {
        return this._isSubscribedSubject.value;
    }

    get isSubscribed$(): Observable<boolean> {
        return this._isSubscribedSubject.asObservable();
    }

    private get subscriptionId(): number | null {
        const id = localStorage.getItem(SUBSCRIPTION_ID_STORAGE_KEY);
        return id ? parseInt(id, 10) : null;
    }

    private set subscriptionId(value: number | null) {
        if (value !== null) {
            localStorage.setItem(SUBSCRIPTION_ID_STORAGE_KEY, value.toString());
        } else {
            localStorage.removeItem(SUBSCRIPTION_ID_STORAGE_KEY);
        }
    }

    async enable(): Promise<boolean> {
        if (!this.isEnabled || !this.config) {
            console.warn("Push notifications are not supported or not configured");
            return false;
        }

        if (this._isEnablingInProgress) {
            console.log("Push notification enable already in progress");
            return false;
        }

        if (this.subscriptionId !== null) {
            const currentSubscription = await firstValueFrom(this.swPush.subscription.pipe(take(1)));
            if (currentSubscription) {
                console.log("Push notifications already enabled with subscription ID:", this.subscriptionId);
                this._isSubscribedSubject.next(true);
                return true;
            }
        }

        // iOS/iPadOS requires a user gesture—call this from a click handler.
        const permission = await Notification.requestPermission();
        if (permission !== "granted") {
            return false;
        }

        this._isEnablingInProgress = true;

        try {
            // Check if there's an existing subscription
            // If there is, clear the stored subscription ID first to prevent
            // any reactive code from using it when the subscription changes
            const currentSubscription = await firstValueFrom(this.swPush.subscription.pipe(take(1)));
            const oldSubscriptionId = currentSubscription ? this.subscriptionId : null;

            if (currentSubscription && oldSubscriptionId !== null) {
                console.log("Push notifications already enabled with subscription ID:", oldSubscriptionId);
                this._isSubscribedSubject.next(true);
                return true;
            }

            // Clear the subscription ID BEFORE calling requestSubscription
            // This prevents any reactive subscriptions from using the old ID
            // when Angular's SwPush replaces the subscription
            if (currentSubscription) {
                this.subscriptionId = null;
            }

            // Subscribe with your VAPID public key (Base64URL)
            // Note: Angular's SwPush will automatically unsubscribe any existing subscription
            // but we've already cleared the stored ID, so no API call will be made
            const sub = await this.swPush.requestSubscription({
                serverPublicKey: this.config.vapidPublicKey,
            });

            if (!sub) {
                console.error("Failed to get push subscription");
                return false;
            }

            const response = await firstValueFrom(
                this.http.post<{ subscriptionId: number; }>(`${this.config.apiBaseUrl}/Push/Subscribe`, sub),
            );

            if (response?.subscriptionId) {
                this.subscriptionId = response.subscriptionId;
            }

            this._isSubscribedSubject.next(true);

            return true;
        } catch (error) {
            console.error("Failed to enable push notifications:", error);
            return false;
        } finally {
            this._isEnablingInProgress = false;
        }
    }

    async disable(): Promise<boolean> {
        if (!this.isSupported || !this.config) {
            return false;
        }

        const storedSubscriptionId = this.subscriptionId;

        try {
            // Get the current subscription synchronously using take(1) to prevent leaks
            const currentSubscription = await firstValueFrom(this.swPush.subscription.pipe(take(1)));

            if (currentSubscription) {
                await currentSubscription.unsubscribe();

                if (storedSubscriptionId !== null) {
                    await firstValueFrom(this.http.post(`${this.config.apiBaseUrl}/Push/Unsubscribe`, {
                        subscriptionId: storedSubscriptionId,
                    }));
                }

                this.subscriptionId = null;
                this._isSubscribedSubject.next(false);
            }

            return true;
        } catch (error) {
            console.error("Failed to disable push notifications:", error);
            return false;
        }
    }

    get subscription(): Observable<PushSubscription | null> {
        return this.swPush.subscription;
    }

    get messages(): Observable<any> {
        return this.swPush.messages;
    }

    /**
     * Get the current subscription object
     */
    async getCurrentSubscription(): Promise<PushSubscription | null> {
        try {
            return new Promise((resolve) => {
                this.swPush.subscription.subscribe({
                    next: (subscription) => resolve(subscription),
                    error: () => resolve(null),
                });
            });
        } catch (error) {
            return null;
        }
    }

    /**
     * Initialize message listening (should be called once in app initialization)
     */
    initializeMessageListening(): void {
        if (!this.isSupported) {
            return;
        }

        // Load stored notifications from IndexedDB on app start
        this.loadStoredNotifications();

        // Listen for messages from the service worker
        if ("serviceWorker" in navigator) {
            navigator.serviceWorker.addEventListener("message", (event) => {
                if (event.data?.type === "PUSH_NOTIFICATION_RECEIVED") {
                    console.log("Push notification received from service worker:", event.data.payload);

                    try {
                        const payload = event.data.payload as PushNotificationPayload;
                        this.store.dispatch(new PushNotificationActions.AddNotification(payload));
                    } catch (error) {
                        console.error("Error processing push notification:", error);
                    }
                }
            });
        }

        // Also listen to SwPush messages (for data messages)
        this.swPush.messages.subscribe({
            next: (msg) => {
                console.log("Push data message received:", msg);

                // Store the notification in NGXS state
                try {
                    const payload = msg as PushNotificationPayload;
                    this.store.dispatch(new PushNotificationActions.AddNotification(payload));
                } catch (error) {
                    console.error("Error processing push notification:", error);
                }
            },
            error: (error) => {
                console.error("Error receiving push message:", error);
            },
        });
    }

    /**
     * Load notifications from IndexedDB
     */
    private async loadStoredNotifications(): Promise<void> {
        try {
            const notifications = await this.getStoredNotificationsFromIndexedDB();
            if (notifications.length > 0) {
                console.log(`Loading ${notifications.length} stored notifications from IndexedDB`);
                this.store.dispatch(new PushNotificationActions.LoadStoredNotifications());

                // Add each stored notification to state
                notifications.forEach((notification) => {
                    const payload: PushNotificationPayload = {
                        notification: {
                            title: notification.title,
                            body: notification.body,
                            icon: notification.icon,
                            badge: notification.badge,
                            tag: notification.tag,
                            renotify: notification.renotify,
                            actions: notification.actions,
                            data: {
                                notificationId: notification.notificationId,
                                url: notification.url,
                                status: notification.status, // Preserve status from IndexedDB
                                receivedAt: notification.receivedAt, // Preserve original received time
                            },
                        },
                    };
                    this.store.dispatch(new PushNotificationActions.AddNotification(payload));
                });
            }
        } catch (error) {
            console.error("Failed to load stored notifications from IndexedDB", error);
        }
    }

    /**
     * Get all notifications from IndexedDB
     */
    private async getStoredNotificationsFromIndexedDB(): Promise<any[]> {
        const DB_NAME = "PushNotificationsDB";
        const DB_VERSION = 1;
        const STORE_NAME = "notifications";

        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onerror = () => {
                console.error("Failed to open IndexedDB");
                resolve([]);
            };

            request.onsuccess = () => {
                const db = request.result;
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    resolve([]);
                    return;
                }

                const transaction = db.transaction([STORE_NAME], "readonly");
                const store = transaction.objectStore(STORE_NAME);
                const getAllRequest = store.getAll();

                getAllRequest.onsuccess = () => resolve(getAllRequest.result || []);
                getAllRequest.onerror = () => {
                    console.error("Failed to get notifications from IndexedDB");
                    resolve([]);
                };
            };

            request.onupgradeneeded = (event: any) => {
                const db = event.target.result;
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    const objectStore = db.createObjectStore(STORE_NAME, { keyPath: "notificationId" });
                    objectStore.createIndex("receivedAt", "receivedAt", { unique: false });
                }
            };
        });
    }

    /**
     * Update notification status in IndexedDB
     */
    async updateNotificationStatusInIndexedDB(notificationId: string, status: any): Promise<void> {
        const DB_NAME = "PushNotificationsDB";
        const DB_VERSION = 1;
        const STORE_NAME = "notifications";

        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onerror = () => reject(request.error);

            request.onsuccess = () => {
                const db = request.result;
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    resolve();
                    return;
                }

                const transaction = db.transaction([STORE_NAME], "readwrite");
                const store = transaction.objectStore(STORE_NAME);
                const getRequest = store.get(notificationId);

                getRequest.onsuccess = () => {
                    const notification = getRequest.result;
                    if (notification) {
                        notification.status = status;
                        const updateRequest = store.put(notification);
                        updateRequest.onsuccess = () => resolve();
                        updateRequest.onerror = () => reject(updateRequest.error);
                    } else {
                        resolve(); // Notification not found, nothing to update
                    }
                };

                getRequest.onerror = () => reject(getRequest.error);
            };
        });
    }

    /**
     * Clear a notification from IndexedDB
     */
    async clearNotificationFromIndexedDB(notificationId: string): Promise<void> {
        const DB_NAME = "PushNotificationsDB";
        const DB_VERSION = 1;
        const STORE_NAME = "notifications";

        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onerror = () => reject(request.error);

            request.onsuccess = () => {
                const db = request.result;
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    resolve();
                    return;
                }

                const transaction = db.transaction([STORE_NAME], "readwrite");
                const store = transaction.objectStore(STORE_NAME);
                const deleteRequest = store.delete(notificationId);

                deleteRequest.onsuccess = () => resolve();
                deleteRequest.onerror = () => reject(deleteRequest.error);
            };
        });
    }

    /**
     * Clear all notifications from IndexedDB
     */
    async clearAllNotificationsFromIndexedDB(): Promise<void> {
        const DB_NAME = "PushNotificationsDB";
        const DB_VERSION = 1;
        const STORE_NAME = "notifications";

        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onerror = () => reject(request.error);

            request.onsuccess = () => {
                const db = request.result;
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    resolve();
                    return;
                }

                const transaction = db.transaction([STORE_NAME], "readwrite");
                const store = transaction.objectStore(STORE_NAME);
                const clearRequest = store.clear();

                clearRequest.onsuccess = () => resolve();
                clearRequest.onerror = () => reject(clearRequest.error);
            };
        });
    }
}
